import axios from 'axios';
import {
  accessToken,
  clearSession,
  refreshToken,
  saveRefreshedTokens,
  sessionIsIdle
} from '../contexts/adminSession.js';
const baseURL = import.meta.env?.VITE_API_URL || '/api/v1';
const api = axios.create({
  baseURL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' }
});
let refreshing = null;
function expired() {
  clearSession();
  window.dispatchEvent(new Event('zenoo-admin-session-expired'));
  if (window.location.pathname !== '/login') window.location.replace('/login');
}
api.interceptors.request.use(config => {
  if (!config.url?.startsWith('/auth/') && sessionIsIdle()) {
    expired();
    return Promise.reject(new Error('Session inactive. Reconnectez-vous.'));
  }
  const token = accessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  config.adminTokenAtStart = token;
  return config;
});
api.interceptors.response.use(
  response => response,
  async error => {
    const original = error.config;
    if (
      error.response?.status !== 401 ||
      !original ||
      original.url?.startsWith('/auth/')
    )
      return Promise.reject(error);
    // A late response from a logged-out account must never overwrite a new login.
    if (original.adminTokenAtStart !== accessToken())
      return Promise.reject(error);
    if (
      original._retried ||
      !accessToken() ||
      !refreshToken() ||
      sessionIsIdle()
    ) {
      expired();
      return Promise.reject(error);
    }
    original._retried = true;
    if (!refreshing) {
      const priorToken = accessToken();
      refreshing = axios
        .post(
          baseURL + '/auth/refresh-token',
          { refreshToken: refreshToken() },
          { timeout: 15000 }
        )
        .then(response => {
          const data = response.data?.data;
          if (!data?.token || !data?.refreshToken)
            throw new Error('Réponse de renouvellement invalide');
          if (accessToken() !== priorToken || sessionIsIdle())
            throw new Error('Session remplacée ou inactive');
          saveRefreshedTokens(data.token, data.refreshToken);
          return data.token;
        })
        .finally(() => {
          refreshing = null;
        });
    }
    try {
      const token = await refreshing;
      original.headers.Authorization = `Bearer ${token}`;
      return api(original);
    } catch (refreshError) {
      if (accessToken() === original.adminTokenAtStart) expired();
      return Promise.reject(refreshError);
    }
  }
);
export default api;
